import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Heart } from 'lucide-react';
import { Card } from '../ui/card';

export const PayClarityArticle = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqs = [
    {
      question: "Why don't construction workers know their paycheck amount before payday?",
      answer: "Most construction workers rely on manual time entry, complex payroll systems, and delayed reporting that prevent real-time pay visibility. PayFlow solves this with instant tracking and calculations."
    },
    {
      question: "How does paycheck uncertainty affect workers?",
      answer: "It causes financial stress, budgeting problems, missed bills, and erosion of trust between workers and employers. Real-time pay clarity eliminates this uncertainty."
    },
    {
      question: "What is pay clarity and why does it matter?",
      answer: "Pay clarity means knowing exactly what your paycheck will be before payday. It's essential for financial planning, reducing stress, and ensuring accurate payment."
    },
    {
      question: "How does PayFlow provide pay clarity?",
      answer: "PayFlow tracks your hours, calculates overtime and per diem in real time, and shows you updated paycheck estimates as you work throughout the week."
    },
    {
      question: "Is pay clarity a standard in other industries?",
      answer: "Yes. Office workers typically have predictable salaries or clear hourly rates with simple calculations. Construction workers deserve the same transparency."
    }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Pay Clarity: Why Tradespeople Deserve to Know Their Check Before Payday",
    "description": "A breakdown of why tradespeople need real-time paycheck visibility and how PayFlow delivers predictable, accurate pay insights for construction workers.",
    "author": {
      "@type": "Organization",
      "name": "ProjectFlow Automations"
    },
    "keywords": "paycheck clarity, predict pay, construction payroll transparency, PayFlow app",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://projectflowautomations.com/knowledge/pay-clarity"
    }
  };

  return (
    <>
      <Helmet>
        <title>Why Tradespeople Need Pay Clarity | PayFlow App</title>
        <meta 
          name="description" 
          content="Discover why paycheck uncertainty affects tradespeople and how PayFlow provides real-time visibility into hours, overtime, and per diem for stress-free payday." 
        />
        <meta name="keywords" content="paycheck clarity app, construction pay transparency, refinery pay issues, track pay before payday, PayFlow construction app, paycheck predictor for trades" />
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0D1115] pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <a href="/knowledge" className="inline-flex items-center text-[#30D5C8] hover:text-[#30D5C8]/80 font-['Open_Sans'] mb-8 transition-colors">
            <ArrowLeft size={20} className="mr-2" />
            Back to Knowledge Center
          </a>

          <div className="mb-12">
            <div className="inline-block bg-[#30D5C8]/10 border border-[#30D5C8]/30 rounded-full px-4 py-2 mb-6">
              <span className="text-[#30D5C8] text-sm font-['Montserrat'] font-semibold">PAY TRANSPARENCY</span>
            </div>
            <h1 className="font-['Montserrat'] font-bold text-4xl sm:text-5xl text-[#F5F7F8] mb-6 leading-tight">
              Pay Clarity: Why Tradespeople Deserve to Know Their Check Before Payday
            </h1>
            <p className="text-[#CBD3D9] text-xl font-['Open_Sans'] leading-relaxed">
              Construction workers are responsible for critical work on tight deadlines — yet most of them still don't know what their paycheck will be until it arrives.
            </p>
          </div>

          <article className="space-y-8">
            <Card className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-8">
              <p className="text-[#CBD3D9] font-['Open_Sans'] text-lg leading-relaxed">
                That uncertainty causes stress, financial strain, and unnecessary conflict.
              </p>
            </Card>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-3xl text-[#30D5C8] mb-6">The Emotional Impact</h2>
              <Card className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-8">
                <div className="flex items-start space-x-4 mb-6">
                  <Heart className="text-[#30D5C8] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <p className="text-[#CBD3D9] font-['Open_Sans'] text-lg mb-4">Workers worry about:</p>
                    <ul className="space-y-3 text-[#CBD3D9] font-['Open_Sans']">
                      <li className="flex items-start"><span className="text-[#30D5C8] mr-3">•</span><span>Missing OT</span></li>
                      <li className="flex items-start"><span className="text-[#30D5C8] mr-3">•</span><span>Missing per diem</span></li>
                      <li className="flex items-start"><span className="text-[#30D5C8] mr-3">•</span><span>Short checks</span></li>
                      <li className="flex items-start"><span className="text-[#30D5C8] mr-3">•</span><span>Payroll errors</span></li>
                      <li className="flex items-start"><span className="text-[#30D5C8] mr-3">•</span><span>Unpredictable totals</span></li>
                    </ul>
                    <p className="text-[#F5F7F8] font-['Open_Sans'] font-semibold text-lg mt-6">Payday becomes a gamble, not a confirmation.</p>
                  </div>
                </div>
              </Card>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-3xl text-[#30D5C8] mb-6">The Financial Impact</h2>
              <Card className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-8">
                <p className="text-[#CBD3D9] font-['Open_Sans'] text-lg mb-6">Wrong paychecks mean:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-[#0D1115]/50 rounded-lg p-4">
                    <p className="text-[#30D5C8] font-['Montserrat'] font-semibold mb-2">Late bills</p>
                    <p className="text-[#CBD3D9] font-['Open_Sans'] text-sm">Financial obligations can't wait for corrections</p>
                  </div>
                  <div className="bg-[#0D1115]/50 rounded-lg p-4">
                    <p className="text-[#30D5C8] font-['Montserrat'] font-semibold mb-2">Missed budgeting</p>
                    <p className="text-[#CBD3D9] font-['Open_Sans'] text-sm">Can't plan without knowing actual income</p>
                  </div>
                  <div className="bg-[#0D1115]/50 rounded-lg p-4">
                    <p className="text-[#30D5C8] font-['Montserrat'] font-semibold mb-2">Lost trust</p>
                    <p className="text-[#CBD3D9] font-['Open_Sans'] text-sm">Erodes worker-employer relationships</p>
                  </div>
                  <div className="bg-[#0D1115]/50 rounded-lg p-4">
                    <p className="text-[#30D5C8] font-['Montserrat'] font-semibold mb-2">Wasted time</p>
                    <p className="text-[#CBD3D9] font-['Open_Sans'] text-sm">Hours spent chasing corrections</p>
                  </div>
                </div>
              </Card>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-3xl text-[#30D5C8] mb-6">Why Pay Clarity Should Be Standard</h2>
              <Card className="bg-[#1A2733]/50 backdrop-blur-sm border-[#30D5C8]/30 p-8">
                <p className="text-[#CBD3D9] font-['Open_Sans'] text-lg mb-6">
                  Tradespeople deserve the same financial visibility office workers get:
                </p>
                <ul className="space-y-3 text-[#CBD3D9] font-['Open_Sans'] text-lg">
                  <li className="flex items-start"><span className="text-[#30D5C8] mr-3">✓</span><span>Real-time tracking</span></li>
                  <li className="flex items-start"><span className="text-[#30D5C8] mr-3">✓</span><span>Updated totals</span></li>
                  <li className="flex items-start"><span className="text-[#30D5C8] mr-3">✓</span><span>Transparent calculations</span></li>
                  <li className="flex items-start"><span className="text-[#30D5C8] mr-3">✓</span><span>Predictable income</span></li>
                </ul>
                <p className="text-[#F5F7F8] font-['Montserrat'] font-bold text-xl mt-6">
                  This isn't only about money — it's about respect.
                </p>
              </Card>
            </section>

            <Card className="bg-gradient-to-br from-[#30D5C8]/20 to-[#30D5C8]/5 border-[#30D5C8]/50 p-8">
              <p className="text-[#F5F7F8] font-['Open_Sans'] text-lg leading-relaxed mb-6">
                PayFlow by ProjectFlow Automations is built for construction and refinery workers who want clarity on hours, overtime, and per diem before payday.
              </p>
              <a href="/payflow" className="inline-block bg-[#30D5C8] text-[#0D1115] hover:bg-[#30D5C8]/90 font-['Montserrat'] font-bold text-lg px-8 py-4 rounded-lg transition-colors">
                Learn More About PayFlow
              </a>
            </Card>
          </article>

          <section className="mt-16">
            <h2 className="font-['Montserrat'] font-bold text-3xl text-[#F5F7F8] mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-6">
                  <h3 className="font-['Montserrat'] font-bold text-lg text-[#30D5C8] mb-3">{faq.question}</h3>
                  <p className="text-[#CBD3D9] font-['Open_Sans'] leading-relaxed">{faq.answer}</p>
                </Card>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};